import config from '../config';

const PUBLISHED_IMAGE_ID_PATTERN = /^pubimg_[A-Za-z0-9-]+$/;
const PUBLIC_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

const configuredApiBase = () => {
  const raw = String(config?.apiV2Url || '/api_v2').trim().replace(/\/+$/, '');
  return raw || '/api_v2';
};

const currentOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return null;
};

const configuredAbsoluteOrigin = () => {
  const raw = String(config?.apiV2Url || '').trim();
  if (!/^(?:https?:)?\/\//i.test(raw)) return null;
  try {
    const parsed = new URL(raw);
    return parsed.username || parsed.password ? null : parsed.origin;
  } catch (_) {
    return null;
  }
};

const parseSameOriginUrl = (value) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  const origin = currentOrigin();
  try {
    const parsed = new URL(raw, origin || 'http://henosis.invalid');
    if (parsed.username || parsed.password) return null;
    const trustedOrigin = configuredAbsoluteOrigin();
    if (origin && parsed.origin !== origin && parsed.origin !== trustedOrigin) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const configuredBasePath = () => {
  const base = configuredApiBase();
  const parsed = parseSameOriginUrl(base);
  if (!parsed) return null;
  return parsed.pathname.replace(/\/+$/, '') || '/';
};

const configuredPrefix = () => {
  const base = configuredApiBase();
  const parsed = parseSameOriginUrl(base);
  if (!parsed) return null;
  return parsed.origin === 'http://henosis.invalid'
    ? parsed.pathname.replace(/\/+$/, '')
    : `${parsed.origin}${parsed.pathname.replace(/\/+$/, '')}`;
};

export const isPublishedImageId = (value) => (
  typeof value === 'string' && PUBLISHED_IMAGE_ID_PATTERN.test(value.trim())
);

export const isPublicImageToken = (value) => (
  typeof value === 'string' && PUBLIC_TOKEN_PATTERN.test(value.trim())
);

export const getPublishedImageId = (block) => {
  const value = typeof block === 'string' ? block : block?.attachment_id;
  const normalized = typeof value === 'string' ? value.trim() : '';
  return isPublishedImageId(normalized) ? normalized : null;
};

export const getPublishedImageIdFromUrl = (value) => {
  const parsed = parseSameOriginUrl(value);
  const match = parsed?.pathname?.match(/\/media\/images\/(pubimg_[A-Za-z0-9-]+)(?:\/download)?$/i);
  const id = match?.[1] || '';
  return isPublishedImageId(id) ? id : null;
};

export const getPublishedPublicTokenFromUrl = (value) => {
  const parsed = parseSameOriginUrl(value);
  const match = parsed?.pathname?.match(/\/media\/public-images\/([A-Za-z0-9_-]+)(?:\/download)?$/i);
  return isPublicImageToken(match?.[1] || '') ? match[1] : null;
};

// Public Markdown references carry only a capability token, not the owner's
// attachment ID. Keep an in-memory-only, valid published-image identity for
// the preview component; public fetches still use the validated token URL.
export const getPublishedInlineImageId = (token) => {
  const normalized = typeof token === 'string' ? token.trim() : '';
  return isPublicImageToken(normalized) ? `pubimg_inline-${normalized}` : null;
};

export const normalizeContentBlocks = (blocks) => (
  Array.isArray(blocks)
    ? blocks.map((block) => (block && typeof block === 'object' ? { ...block } : block))
    : null
);

const publicationSuffix = (kind, id, token = null) => {
  if (!isPublishedImageId(id)) return null;
  if (kind === 'image') return `/media/images/${encodeURIComponent(id)}`;
  if (kind === 'download') return `/media/images/${encodeURIComponent(id)}/download`;
  if (kind === 'public' || kind === 'public_download') return '/media/public-images';
  return null;
};

const isAllowedPublicationPath = (pathname, suffix) => {
  if (!pathname || !suffix) return false;
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const basePath = configuredBasePath();
  // FastAPI URLs can be returned backend-relative as /api/..., while the
  // production gateway exposes the same FastAPI routes at /api_v2/....
  // Validate the route shape, then always return the configured v2 prefix.
  return normalizedPath === `/api${suffix}`
    || normalizedPath === `/api_v2${suffix}`
    || (!!basePath && normalizedPath === `${basePath}${suffix}`);
};

export const rebasePublishedImageUrl = (value, kind, id, token = null) => {
  const suffix = publicationSuffix(kind, id, token);
  const prefix = configuredPrefix();
  if (!suffix || !prefix) return null;
  let resolvedSuffix = suffix;
  if (kind === 'public' || kind === 'public_download') {
    const parsed = value != null ? parseSameOriginUrl(value) : null;
    const candidatePath = parsed?.pathname?.replace(/\/+$/, '') || '';
    const publicPathPattern = kind === 'public'
      ? /\/media\/public-images\/([^/]+)$/
      : /\/media\/public-images\/([^/]+)(?:\/download)?$/;
    const candidateMatch = candidatePath.match(publicPathPattern);
    let candidateToken = candidateMatch?.[1] || '';
    try { candidateToken = decodeURIComponent(candidateToken); } catch (_) { return null; }
    const explicitToken = typeof token === 'string' ? token.trim() : '';
    if (explicitToken && candidateToken && explicitToken !== candidateToken) return null;
    const resolvedToken = candidateToken || explicitToken;
    if (!isPublicImageToken(resolvedToken)) return null;
    if (value != null && (!parsed || parsed.search || parsed.hash)) return null;
    if (value != null && !isAllowedPublicationPath(candidatePath.replace(publicPathPattern, '/media/public-images'), suffix)) return null;
    resolvedSuffix = `/media/public-images/${encodeURIComponent(resolvedToken)}${kind === 'public_download' ? '/download' : ''}`;
  } else if (value != null) {
    const parsed = parseSameOriginUrl(value);
    if (!parsed || !isAllowedPublicationPath(parsed.pathname, suffix) || parsed.search || parsed.hash) return null;
  }
  return `${prefix}${resolvedSuffix}`;
};

export const getPublishedImageUrl = (block, kind = 'image') => {
  const id = getPublishedImageId(block);
  if (!id) return null;
  if (kind === 'public' || kind === 'public_download') {
    const raw = kind === 'public'
      ? block?.public_url
      : (block?.public_download_url || block?.public_url);
    const token = block?.public_token;
    const resolved = rebasePublishedImageUrl(raw, kind, id, token);
    if (kind === 'public_download' && block?.public_url) {
      const preview = rebasePublishedImageUrl(block.public_url, 'public', id, token);
      if (!preview || resolved !== `${preview}/download`) return null;
    }
    return resolved;
  }
  const raw = kind === 'download' ? block?.download_url : block?.url;
  return rebasePublishedImageUrl(raw, kind, id);
};

export const getPublishedImageMetadataUrl = (id) => {
  const prefix = configuredPrefix();
  return prefix && isPublishedImageId(id)
    ? `${prefix}/media/published-images/${encodeURIComponent(id.trim())}`
    : null;
};

export const getPublicationVisibility = (block) => {
  const visibility = String(block?.visibility || '').trim().toLowerCase();
  return visibility === 'public' ? 'public' : 'private';
};

export const getPublishedImageLabel = (block) => (
  block?.caption || block?.filename || block?.fileName || 'Published image'
);

export const getPublishedImageFilename = (block) => (
  block?.filename || block?.fileName || 'published-image'
);
